/**
 * @file
 * Send whitelisted stats to the server with ClientStats. You can add stats in
 * okcontent/dictionaries/clientstats.dict
 */

import OkC from "shared/util/OkcApi";
import Context from "shared/util/Context";
import isApp from "shared/util/isApp";
import {
	isDesktop,
	isMobile,
	isIOS,
	isAndroid,
} from "shared/util/EnvironmentHelpers";

const STAT_SEND_DELAY = 10;
let sendTimeout = null;

const ClientStats = {
	bufferedStats: [],
	bufferedCbs: [],
	uniqueQueued: {},
	uniqueStats: {},

	/**
	 * Queue up a stat to be added to the next request. This does NOT start a
	 * request, only adds it to the next time one is going to fire. There's no
	 * guarantee a stat queued by add() will fire.
	 *
	 * @param {string|string[]} stat - A stat name string, or array of strings.
	 */
	add(stat) {
		if (stat.constructor === Array) {
			this.bufferedStats = this.bufferedStats.concat(stat);
		}
		else {
			this.bufferedStats.push(stat);
		}
	},

	/**
	 * Queue up a stat to be fired once and only once per session. Behaves exactly
	 * the same as `add`, only it makes sure this stat has never been queued
	 * before.
	 *
	 * @param {string|string[]} stat - A stat name string, or array of strings.
	 */
	addUnique(stat) {
		if (stat.constructor === Array) {
			stat.forEach((s) => {
				this.uniqueQueued[s] = true;
			});
		}
		else {
			this.uniqueQueued[stat] = true;
		}

		this.add(stat);
	},

	/**
	 * Prepends platform before calling `ClientStats.update`.
	 *
	 * @param {Object|string|string[]} params - Either a string stat name, an
	 *   array of stats, or an object with certain keys.
	 * @param {string}   params.statname - A single stat to fire.
	 * @param {string[]} params.statnames - An array of stats to fire.
	 * @param {function} params.cb - A callback to fire after send finishes.
	 * @param {bool}     params.skipDelay - Bypass the delay on sending, fire now.
	 * @param {bool}     params.unique - Whether or not these stats should only
	 *                                   fire once per session. @see `addUnique`.
	 */
	updateWithPlatform(params = {}) {
		const platform = (() => {
			if (isApp()) {
				if (isIOS) {
					return "App - iPhone";
				} else if (isAndroid) {
					return "App - Android";
				}

				return "App - Unknown";
			} else if (isDesktop) {
				return "desktop";
			} else if (isMobile) {
				return "mobile site";
			}

			return "unknown";
		})();

		if (Array.isArray(params)) {
			const updatedStats = params.map((statName) => {
				return `${platform} - ${statName}`;
			});
			ClientStats.update(updatedStats);
		} else if (typeof params === "string") {
			ClientStats.update(`${platform} - ${params}`);
		} else {
			const updatedParams = { ...params };

			if (params.statname) {
				updatedParams.statname = `${platform} - ${params.statname}`;
			}

			if (params.statnames) {
				updatedParams.statnames = params.statnames.map((statName) => {
					return `${platform} - ${statName}`;
				});
			}

			ClientStats.update(updatedParams);
		}
	},

	/**
	 * Add stats and trigger a request. By default the request happens after a
	 * timeout, in case anything else fires an update() or add() call.
	 *
	 * @param {Object|string|string[]} params - Either a string stat name, an
	 *   array of stats, or an object with certain keys.
	 * @param {string}   params.statname - A single stat to fire.
	 * @param {string[]} params.statnames - An array of stats to fire.
	 * @param {function} params.cb - A callback to fire after send finishes.
	 * @param {bool}     params.skipDelay - Bypass the delay on sending, fire now.
	 * @param {bool}     params.unique - Whether or not these stats should only
	 *                                   fire once per session. @see `addUnique`.
	 */
	update(params = {}) {
		const add = params.unique ? this.addUnique.bind(this) : this.add.bind(this);

		if (params.constructor === Array || typeof params === "string") {
			add(params);
		}
		else {
			if (params.statname) {
				add(params.statname);
			}

			if (params.statnames) {
				add(params.statnames);
			}

			if (params.cb) {
				this.bufferedCbs.push(params.cb);
			}
		}

		this.send(params.skipDelay);
	},

	/**
	 * Trigger a send of stats in the queue, after a delay by default. Use add()
	 * and update() if you want to actually pass stats to be sent.
	 *
	 * @param {bool} skipDelay - Bypass the delay on sending, fire now.
	 */
	send(skipDelay) {
		clearTimeout(sendTimeout);
		sendTimeout = setTimeout(() => {
			// Don't bother with no stats
			if (!this.bufferedStats.length) {
				this.clearBuffersAndFireCallbacks(null);

				return;
			}

			// Don't post stats in production for staff
			if (Context && Context.get("env") !== "dev" && Context.get("isStaff")) {
				this.clearBuffersAndFireCallbacks(null);

				return;
			}

			// Don't fire unique stats
			const statsToSend = [];

			this.bufferedStats.forEach((stat) => {
				if (this.uniqueStats[stat]) {
					return;
				}

				statsToSend.push(stat);

				if (this.uniqueQueued[stat]) {
					this.uniqueStats[stat] = true;
				}
			});

			// Shoot off the request
			if (statsToSend.length) {
				OkC.post("stats/update", {
					data: {
						stats: statsToSend,
					},
				})
					.then((res) => {
						this.clearBuffersAndFireCallbacks(res);
					})
					.catch((err) => {
						this.clearBuffersAndFireCallbacks(err);
					});
			}
			else {
				this.clearBuffersAndFireCallbacks(null);
			}
		}, skipDelay ? STAT_SEND_DELAY : 0);
	},

	clearBuffersAndFireCallbacks(args) {
		// Cache callbacks and clear the buffers
		const cbs = [...this.bufferedCbs];
		const triggerCbs = (res) => cbs.forEach((fn) => fn(res));
		this.bufferedStats = [];
		this.bufferedCbs = [];

		triggerCbs(args);
	},
};

export default ClientStats;

// A lot of apps reference this nakedly and potentially expect it before
// imports resolve. Therefore, we need to bind this to window ASAP internally.
// Sorry, y'all.
if (typeof window !== "undefined" && window !== null && !window.ClientStats) {
	window.ClientStats = ClientStats;
}



// WEBPACK FOOTER //
// ./src/shared/util/ClientStats.js